<template>
  <div class="py-4 container-fluid">
    <div class="row mb-3">
      <div class="col-12 d-flex flex-wrap justify-content-between align-items-center">
        <div>
          <h5 class="mb-0">{{ $t('reports.toolUtilizationTitle') }}</h5>
          <p class="mb-0 text-sm text-secondary">{{ $t('reports.toolUtilizationSubtitle') }}</p>
        </div>
        <material-button variant="outline" color="dark" size="sm" :disabled="!rows.length" @click="onExport">
          <i class="material-icons text-sm me-1">download</i>{{ $t('reports.exportCsv') }}
        </material-button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card mb-3">
      <div class="card-body d-flex flex-wrap gap-3 align-items-end">
        <div>
          <label class="text-xs text-secondary d-block mb-1">{{ $t('common.from') }}</label>
          <el-date-picker v-model="fromDate" type="date" value-format="YYYY-MM-DD" format="DD/MM/YYYY" :clearable="false" @change="reload" />
        </div>
        <div>
          <label class="text-xs text-secondary d-block mb-1">{{ $t('common.to') }}</label>
          <el-date-picker v-model="toDate" type="date" value-format="YYYY-MM-DD" format="DD/MM/YYYY" :clearable="false" @change="reload" />
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-secondary" role="status"></div>
        </div>
        <template v-else>
          <el-table :data="rows" stripe>
            <el-table-column prop="toolName" :label="$t('reports.tool')" min-width="160" />
            <el-table-column prop="toolType" :label="$t('reports.toolType')" min-width="120" />
            <el-table-column prop="location" :label="$t('reports.location')" min-width="120" />
            <el-table-column prop="reservationCount" :label="$t('reports.reservations')" align="center" min-width="100" />
            <el-table-column prop="reservedDays" :label="$t('reports.reservedDays')" align="center" min-width="100" />
            <el-table-column prop="idleDays" :label="$t('reports.idleDays')" align="center" min-width="100" />
            <el-table-column :label="$t('reports.utilization')" min-width="160">
              <template #default="{ row }">
                <el-progress :percentage="Math.min(100, row.utilizationPercent)" :color="utilColor(row.utilizationPercent)" :stroke-width="14" :text-inside="true" />
              </template>
            </el-table-column>
          </el-table>

          <div class="d-flex justify-content-between align-items-center mt-3">
            <span class="text-sm text-secondary">{{ $t('reports.periodDays', { days: periodDays }) }}</span>
            <el-pagination
              v-model:current-page="currentPage"
              :page-size="pageSize"
              :total="totalItems"
              layout="prev, pager, next"
              @current-change="fetchData"
            />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../../services/api";
import MaterialButton from "@/components/MaterialButton.vue";
import { exportCsv } from "../../utils/csvExport";

export default {
  name: "ToolUtilizationReport",
  components: { MaterialButton },
  data() {
    const today = new Date();
    const from = new Date();
    from.setDate(today.getDate() - 29);
    return {
      fromDate: this.toIso(from),
      toDate: this.toIso(today),
      rows: [],
      totalItems: 0,
      periodDays: 0,
      currentPage: 1,
      pageSize: 20,
      loading: true,
    };
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    toIso(d) {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    },
    utilColor(p) {
      if (p >= 60) return "#2dce89";
      if (p >= 25) return "#fb8c00";
      return "#f5365c";
    },
    reload() {
      this.currentPage = 1;
      this.fetchData();
    },
    async fetchData() {
      this.loading = true;
      try {
        const response = await api.get("/Analytics/Tools/Utilization", {
          params: { fromDate: this.fromDate, toDate: this.toDate, currentPage: this.currentPage, pageSize: this.pageSize },
        });
        this.rows = response.data.result || [];
        this.totalItems = response.data.totalItems || 0;
        this.periodDays = response.data.periodDays || 0;
      } catch (e) {
        console.error("ToolUtilizationReport fetchData error:", e); // eslint-disable-line no-console
        this.$toast.error(this.$t("common.unableToRetrieveData"), { timeout: 0 });
      } finally {
        this.loading = false;
      }
    },
    onExport() {
      const columns = [
        { key: "toolName", label: this.$t("reports.tool") },
        { key: "toolType", label: this.$t("reports.toolType") },
        { key: "location", label: this.$t("reports.location") },
        { key: "reservationCount", label: this.$t("reports.reservations") },
        { key: "reservedDays", label: this.$t("reports.reservedDays") },
        { key: "idleDays", label: this.$t("reports.idleDays") },
        { key: "utilizationPercent", label: this.$t("reports.utilization") },
      ];
      exportCsv(`tool-utilization-${this.fromDate}_${this.toDate}`, columns, this.rows);
    },
  },
};
</script>
